/**
 * @file BannerPhotoGalleryPage.js
 * @module src/js/pages/BannerPhotoGalleryPage
 * @description
 * **Page component for displaying and managing banner photos.**
 * - Fetches banner photo data from the backend.
 * - Uses a grid layout for displaying images.
 * - Allows authorized users to upload and delete banner photos.
 *
 * @version
 * <p>
 *  2025-11-29: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-01: Change border radius from 5% to 5px. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Use grid layout for displaying images. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Migrate to useDeleteEntry. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useUser } from "../contexts/UserProvider";
import { useTheme } from "../contexts/ThemeProvider";
import useFetchFromBackend from "../hooks/useFetchFromBackend";
import useDeleteEntry from "../hooks/useDeleteEntry";
import NumberPagination from "../components/NumberPagination";
import PagedTable from "../components/PagedTable";
import SpanARow from "../components/SpanARow";
import BannerUploadForm from "../components/BannerUploadForm";
import { StyledImg } from "../components/UtilityComponents";

export default function BannerPhotoGalleryPage() {
    const { pathname } = useLocation();
    const { uid } = useUser();
    const { theme } = useTheme();
    const fetchFromBackend = useFetchFromBackend();
    const [bannerImages, setBannerImages] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [currentFirstPage, setCurrentFirstPage] = useState(1);
    const [deleting, setDeleting] = useState(false);
    const [loading, setLoading] = useState(true);
    const headerRef = useRef(null);
    const deleteEntry = useDeleteEntry({ setData: setBannerImages, setDeleting });

    const numPageButtons = 10;
    const numItemsPerPage = 10;

    const bannerImagesOnCurrentPage = useMemo(() => {
        if (!bannerImages || bannerImages.length === 0) return [];
        const start = (currentPage - 1) * numItemsPerPage;
        const end = currentPage * numItemsPerPage;
        return bannerImages.slice(start, end);
    }, [bannerImages, currentPage]);
    
    useEffect(() => {
        async function fetchData() {
            const data = await fetchFromBackend({ request: '/api/banners' });
            setBannerImages(data || []);
            setLoading(false);
        }
        fetchData();
    }, [fetchFromBackend]);

    const numberPagination = (
        <NumberPagination
            length={bannerImages.length}
            numPageButtons={numPageButtons}
            numItemsPerPage={numItemsPerPage}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            currentFirstPage={currentFirstPage}
            setCurrentFirstPage={setCurrentFirstPage}
            headerRef={headerRef}
        />
    );

    const table = (
        <div className="table-wrapper">
            <table>
                <colgroup>
                    <col style={{ width: '100%' }} />
                </colgroup>
                <thead>
                    <tr>
                        <th className='hori-vert-center' colSpan={uid === 'Guest' ? 1 : 2}>Image</th>
                    </tr>
                </thead>
                <tbody>
                    <BannerUploadForm setData={setBannerImages} />
                    {bannerImagesOnCurrentPage.length === 0 && (
                        <SpanARow
                            colSpan={uid === 'Guest' ? 1 : 2}
                            text="No banner photos found"
                            loading={loading}
                        />
                    )}
                    {bannerImagesOnCurrentPage.length > 0 && (
                        <tr>
                            <td colSpan={uid === 'Guest' ? 1 : 2}>
                                <div style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                                    gap: "16px",
                                }}>
                                    {bannerImagesOnCurrentPage.map((item, i) => (
                                        <div key={i} style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: "8px",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}>
                                            <StyledImg
                                                src={item}
                                                style={{
                                                    width: '100%',
                                                    height: 'auto',
                                                    borderRadius: '5px',
                                                    boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.2)'
                                                }}
                                            />
                                            {uid !== 'Guest' && (
                                                <button
                                                    onClick={() => deleteEntry({
                                                        request: "/api/banners",
                                                        id: item.split('/').pop(),
                                                        from: pathname,
                                                    })}
                                                    disabled={deleting}
                                                    style={{
                                                        color: theme === 'dark' ? 'lightcoral' : 'red',
                                                        outline: `2px solid ${theme === 'dark' ? 'lightcoral' : 'rgba(255, 0, 0, 0.1)'}`,
                                                        outlineOffset: '-2px',
                                                    }}
                                                >
                                                    Delete
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </td>   
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );

    return (
        <PagedTable
            header="Banner Photo Gallery"
            headerRef={headerRef}
            numberPagination={bannerImages.length > numItemsPerPage ? numberPagination : null}
            table={table}
        />
    )
}
